import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import cmsApi from "../services/cmsApi";
import { getImageUrl, FALLBACK_IMAGE_SVG } from "../utils/getImageUrl";

const AllNews = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Read query from URL to support navbar search
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("query");
    if (q) setSearchTerm(q);
  }, []);

  // Fetch news from CMS API (single source of truth)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const fetchNews = async () => {
      try {
        setLoading(true);
        setLoadError("");
        // Fetch all news from CMS (excluding breaking news for this page)
        // Database is the single source of truth - no hardcoded fallback
        const result = await cmsApi.getNews({ limit: 100, breaking: 'false' });
        
        if (result.success && result.news && result.news.length > 0) {
          // Transform CMS news to match expected format
          const transformedNews = result.news.map(article => ({
            id: article.id,
            slug: article.slug || article.id,
            title: article.title,
            description: article.description || article.excerpt || '',
            excerpt: article.excerpt || article.description || '',
            content: article.content || article.description || '',
            image: getImageUrl(article.image || article.featuredImage || ''),
            featuredImage: getImageUrl(article.featuredImage || article.image || ''),
            category: article.category || 'other',
            tags: article.tags || [],
            date: article.date || (article.publishedAt ? new Date(article.publishedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
            publishedAt: article.publishedAt,
            author: article.author || 'JRIIT Admin',
            readTime: article.readTime || '5 min read',
            featured: article.featured || false,
            priority: article.priority || 'normal',
            isBreaking: article.isBreaking || false,
          }));
          setNews(transformedNews);
        } else {
          // No news found in database - show empty state
          // This ensures only database items are displayed
          setNews([]);
          setLoadError("No news available right now.");
        }
      } catch (error) {
        setNews([]);
        setLoadError("Unable to load news right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const [selectedTag, setSelectedTag] = useState("all");

  const categories = [{ id: "all", name: "All News" }].concat(
    [...new Set(news.map((item) => item.category).filter(Boolean))].map((category) => ({
      id: category,
      name: category,
    }))
  );

  const tags = [{ id: "all", name: "All Tags" }].concat(
    [...new Set(news.flatMap((item) => item.tags || []).filter(Boolean))].map((tag) => ({
      id: tag,
      name: tag,
    }))
  );

  const filteredNews = news.filter(item => {
    const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
    const matchesTag = selectedTag === "all" || item.tags.includes(selectedTag);
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesTag && matchesSearch;
  });

  const featuredNews = news.filter(item => item.featured);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="mid-desktop-page">
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-custom-pink border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading news...</p>
        </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="mid-desktop-page">
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            Campus <span className="text-gold">News</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Stay informed with the latest updates, achievements, and stories from JRIIT
          </p>
        </div>
      </section>

      {/* Featured News Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-custom-pink mb-4">
              Featured News
            </h2>
            <div className="w-32 h-1 bg-gold mx-auto rounded-full"></div>
          {!loading && loadError && <p className="mt-4 text-sm text-red-500">{loadError}</p>}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {featuredNews.map((item) => (
              <div
                key={item.id}
                className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={getImageUrl(item.image || item.featuredImage || '')}
                    alt={item.title}
                    className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110"
                    onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE_SVG; }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-gold text-black text-sm font-bold rounded-full">
                      Featured
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-custom-pink text-white text-sm font-bold rounded-full">
                      {item.date}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {item.readTime}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {item.author}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-custom-pink transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {item.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {item.tags.map((tag) => (
                      <span key={tag} className="px-2 py-1 bg-custom-pink/10 text-custom-pink text-xs rounded-full">
                        {tags.find(t => t.id === tag)?.name || tag}
                      </span>
                    ))}
                  </div>
                  
                  <Link
                    to={`/news/${item.slug || item.id}`}
                    className="inline-block bg-custom-pink text-white px-6 py-3 rounded-full font-semibold hover:bg-purple-700 transition-colors duration-300 transform hover:scale-105"
                  >
                    Read Full Article
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-gray-100 border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
            {/* Search Bar */}
            <div className="relative w-full lg:w-96">
              <input
                type="text"
                placeholder="Search news..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-custom-pink focus:border-transparent"
              />
              <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Category Filters */}
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                      selectedCategory === category.id
                        ? "bg-custom-pink text-white shadow-lg transform scale-105"
                        : "bg-white text-gray-700 hover:bg-gray-200 hover:text-custom-pink border border-gray-200"
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              {/* Tag Filters */}
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => setSelectedTag(tag.id)}
                    className={`px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      selectedTag === tag.id
                        ? "bg-gold text-black shadow-lg transform scale-105"
                        : "bg-white text-gray-700 hover:bg-gray-200 hover:text-gold border border-gray-200"
                    }`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All News Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              All News ({filteredNews.length})
            </h2>
            <div className="text-sm text-gray-600">
              Showing {filteredNews.length} of {news.length} articles
            </div>
          </div>

          {filteredNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((item) => (
                <div
                  key={item.id}
                  className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={getImageUrl(item.image || item.featuredImage || '')}
                      alt={item.title}
                      className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE_SVG; }}
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-custom-pink text-white text-sm font-bold rounded-full">
                        {item.date}
                      </span>
                    </div>
                    {item.featured && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-gold text-black text-sm font-bold rounded-full">
                          Featured
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {item.readTime}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {item.author}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-custom-pink transition-colors duration-300 line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3 text-sm">
                      {item.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-1 bg-custom-pink/10 text-custom-pink text-xs rounded-full">
                          {tags.find(t => t.id === tag)?.name || tag}
                        </span>
                      ))}
                    </div>
                    
                    <Link
                      to={`/news/${item.slug || item.id}`}
                      className="inline-block bg-custom-pink text-white px-5 py-2 rounded-full font-semibold hover:bg-purple-700 transition-colors duration-300 transform hover:scale-105 text-sm"
                    >
                      Read More
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No news found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-16 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Stay Updated with JRIIT News
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter and get the latest news, events, and updates delivered to your inbox!
          </p>
          <Link to="/contact" className="inline-block bg-gold text-black px-8 py-3 rounded-full font-semibold hover:bg-yellow-400 transition-colors duration-300 transform hover:scale-105">
              Contact Us
            </Link>
        </div>
      </section>

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default AllNews;
